// =============================================================================
// rosterScope.js — how much of the roster a request needs to carry (Sep 2026)
// =============================================================================
// The browser accumulates every shift it has ever loaded — including the whole
// published roster, fetched at launch — and used to upload ALL of it with every
// solve and conflict check (2 MB and growing every week), then receive it all
// back once a second while a solve ran.
//
// A solve or check only needs the window plus the context the rules can see:
//   - before: 4 weeks, the longest fairness lookback. The server also fills in
//     published history for that span itself (attach_published_history), so
//     this only matters for UNPUBLISHED drafts near the window.
//   - after: at least a week — runs, rest and ISO-week caps look forward, and a
//     week straddling the window's end must be seen whole.
//   - and always the whole calendar month at each end, for month-window caps.
// =============================================================================

import { toISODate, fromISODate } from './draftDates';

const LOOKBACK_DAYS = 28;
const LOOKAHEAD_DAYS = 7;

const shiftDate = (s) => String((s && s.start) || '').slice(0, 10);

/** The [from, to] ISO dates a request about [startISO, endISO] must carry. */
export function contextRange(startISO, endISO) {
  const start = fromISODate(startISO.slice(0, 10));
  const end = fromISODate(endISO.slice(0, 10));
  const from = new Date(start); from.setDate(from.getDate() - LOOKBACK_DAYS);
  const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
  const to = new Date(end); to.setDate(to.getDate() + LOOKAHEAD_DAYS);
  const monthEnd = new Date(end.getFullYear(), end.getMonth() + 1, 0);
  return [toISODate(from < monthStart ? from : monthStart), toISODate(to > monthEnd ? to : monthEnd)];
}

/**
 * The shifts a solve or check about [startISO, endISO] needs. With no window
 * the server scores everything it is sent, so everything is returned.
 */
export function scopeShifts(shifts, startISO, endISO) {
  if (!Array.isArray(shifts) || !startISO || !endISO) return shifts;
  const [from, to] = contextRange(startISO, endISO);
  return shifts.filter((s) => { const d = shiftDate(s); return d >= from && d <= to; });
}

/** Just the days in [startISO, endISO], for requests that read nothing else (Excel). */
export function shiftsBetween(shifts, startISO, endISO) {
  if (!Array.isArray(shifts)) return shifts;
  return shifts.filter((s) => { const d = shiftDate(s); return d >= startISO && d <= endISO; });
}

/**
 * Fold a solve result back into the browser's roster.
 *
 * Inside the solve window the result wins — that is what was solved. Outside
 * it the solver changes nothing (those shifts are pinned), but the result can
 * still CARRY shifts the browser holds as unpublished drafts: the server fills
 * in published history for days the request did not send. Letting those
 * overwrite would silently revert the drafts, so outside the window a shift the
 * browser already has is kept as-is and only genuinely new ones are added.
 */
export function mergeSolvedShifts(prevShifts, solvedShifts, windowStartISO, windowEndISO) {
  const prev = prevShifts || [];
  const solved = solvedShifts || [];
  const inWindow = (s) => {
    if (!windowStartISO || !windowEndISO) return true;     // no window: old behaviour
    const d = shiftDate(s);
    return d >= String(windowStartISO).slice(0, 10) && d <= String(windowEndISO).slice(0, 10);
  };
  const have = new Set(prev.map((s) => s.id));
  const take = solved.filter((s) => inWindow(s) || !have.has(s.id));
  const takeIds = new Set(take.map((s) => s.id));
  return [...prev.filter((s) => !takeIds.has(s.id)), ...take];
}
